import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { ArrowLeftRight, ArrowRight, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { bankAccountsQueryOptions } from '@/features/bank-accounts/api'
import { formatBusinessDate } from '@/features/schedules/components/date-text'
import { MoneyText } from '@/features/schedules/components/money-text'
import { transfersQueryOptions } from '@/features/transfers/api'
import {
  TRANSFERENCIAS_FILTROS,
  TRANSFERENCIAS_ORDEM_DEFAULT,
  toTransferListParams,
  transferenciasSearchSchema,
  type TransferenciasSearch,
} from '@/features/transfers/search'
import { TransferFormDrawer } from '@/features/transfers/transfer-form-drawer'
import type { TransferOut } from '@/shared/api/types'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { SelectNative } from '@/shared/ui/select-native'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { DataTable } from '@/widgets/data-table/data-table'
import { NoResultsState } from '@/widgets/data-table/no-results-state'
import { TablePager } from '@/widgets/data-table/table-pager'
import { fromSortingState, toSortingState } from '@/widgets/data-table/table-search'
import {
  FiltroCampo,
  FiltroData,
  TableToolbar,
  type FiltroChip,
} from '@/widgets/data-table/table-toolbar'

export const Route = createFileRoute('/_authed/relatorios/transferencias')({
  validateSearch: transferenciasSearchSchema,
  loaderDeps: ({ search }) => ({ params: toTransferListParams(search) }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(transfersQueryOptions(deps.params)),
  component: TransferenciasPage,
})

const col = createColumnHelper<TransferOut>()

function TransferenciasPage() {
  const { t, i18n } = useTranslation(['transfers', 'nav', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [drawerAberto, setDrawerAberto] = useState(false)
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    transfersQueryOptions(toTransferListParams(search)),
  )
  const contas = useQuery(bankAccountsQueryOptions())

  const aplicar = (patch: Partial<TransferenciasSearch>) => {
    // `to` EXPLÍCITO + `prev` ANOTADO: sem os dois o destino fica ambíguo, o
    // updater cai em `any` (TS7006) e a navegação pode virar no-op silencioso.
    void navigate({
      to: '/relatorios/transferencias',
      search: (prev: TransferenciasSearch) => ({ ...prev, ...patch }),
      replace: true,
    })
  }
  /** Filtrar/ordenar volta para a pagina 1 - senao abre num offset vazio. */
  const filtrar = (patch: Partial<TransferenciasSearch>) => {
    aplicar({ ...patch, pagina: 1 })
  }
  const limparFiltros = () => {
    filtrar(Object.fromEntries(TRANSFERENCIAS_FILTROS.map((k) => [k, undefined])))
  }

  const chips: FiltroChip[] = []
  if (search.q !== undefined) {
    chips.push({
      id: 'q',
      label: `${t('transfers:columns.description')}: ${search.q}`,
      onRemove: () => {
        filtrar({ q: undefined })
      },
    })
  }
  if (search.conta !== undefined) {
    const nome = contas.data?.items.find((c) => c.id === search.conta)?.name ?? search.conta
    chips.push({
      id: 'conta',
      label: `${t('transfers:filters.account')}: ${nome}`,
      onRemove: () => {
        filtrar({ conta: undefined })
      },
    })
  }
  if (search.de !== undefined || search.ate !== undefined) {
    const de = search.de === undefined ? '…' : formatBusinessDate(search.de, i18n.language)
    const ate = search.ate === undefined ? '…' : formatBusinessDate(search.ate, i18n.language)
    chips.push({
      id: 'periodo',
      label: `${t('transfers:columns.date')}: ${de} – ${ate}`,
      onRemove: () => {
        filtrar({ de: undefined, ate: undefined })
      },
    })
  }

  const columns = useMemo(
    () =>
      [
        col.accessor('transfer_date', {
          id: 'transfer_date',
          header: t('transfers:columns.date'),
          cell: (c) => formatBusinessDate(c.getValue(), i18n.language),
          enableSorting: true,
        }),
        col.display({
          id: 'route',
          // Rota e origem->destino, duas colunas do banco: nao ha um `order_by`
          // que signifique isso. Header nao-clicavel e honesto.
          enableSorting: false,
          header: t('transfers:columns.route'),
          cell: (c) => {
            const tr = c.row.original
            return (
              <span className="flex items-center gap-1.5">
                <span className="text-ink">{tr.from_bank_account.name}</span>
                <ArrowRight
                  className="text-ink-subtle size-3.5 shrink-0"
                  aria-label={t('transfers:toLabel')}
                />
                <span className="text-ink">{tr.to_bank_account.name}</span>
              </span>
            )
          },
        }),
        col.accessor('amount', {
          id: 'amount',
          header: t('transfers:columns.amount'),
          cell: (c) => <MoneyText value={c.getValue()} />,
          enableSorting: true,
        }),
        col.accessor((r) => r.description, {
          id: 'description',
          header: t('transfers:columns.description'),
          cell: (c) => c.getValue() ?? '—',
          // `description` nao esta na allowlist de order_by do backend.
          enableSorting: false,
        }),
      ] as ColumnDef<TransferOut>[],
    [t, i18n.language],
  )

  const botaoNovo = (
    <Button
      variant="primary"
      onClick={() => {
        setDrawerAberto(true)
      }}
    >
      <Plus aria-hidden />
      {t('transfers:new')}
    </Button>
  )

  return (
    <>
      <PageHeader title={t('nav:reports.transfers')} actions={botaoNovo} />
      <div className="flex flex-col gap-4 p-6">
        <TableToolbar
          inline
          busca={search.q}
          onBuscaChange={(q) => {
            filtrar({ q })
          }}
          buscaPlaceholder={t('transfers:filters.searchPlaceholder')}
          chips={chips}
          onLimpar={limparFiltros}
        >
          <FiltroCampo id="filtro-conta" label={t('transfers:filters.account')} className="w-48">
            <SelectNative
              id="filtro-conta"
              value={search.conta ?? ''}
              onChange={(e) => {
                filtrar({ conta: e.target.value === '' ? undefined : e.target.value })
              }}
            >
              <option value="">{t('common:toolbar.all')}</option>
              {(contas.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </SelectNative>
          </FiltroCampo>
          <FiltroCampo id="filtro-de" label={t('common:toolbar.from')} className="w-40">
            <FiltroData
              id="filtro-de"
              value={search.de}
              onCommit={(v) => {
                filtrar({ de: v })
              }}
            />
          </FiltroCampo>
          <FiltroCampo id="filtro-ate" label={t('common:toolbar.to')} className="w-40">
            <FiltroData
              id="filtro-ate"
              value={search.ate}
              onCommit={(v) => {
                filtrar({ ate: v })
              }}
            />
          </FiltroCampo>
        </TableToolbar>

        <DataTable
          columns={columns}
          page={data}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          onRetry={() => void refetch()}
          getRowId={(r) => r.id}
          sorting={toSortingState(search.ordenar)}
          onSortingChange={(s) => {
            filtrar({ ordenar: fromSortingState(s, TRANSFERENCIAS_ORDEM_DEFAULT) })
          }}
          footer={
            <TablePager
              total={data?.total ?? 0}
              pagina={search.pagina}
              linhas={search.linhas}
              onPageChange={(pagina) => {
                aplicar({ pagina })
              }}
              onLinhasChange={(linhas) => {
                aplicar({ linhas, pagina: 1 })
              }}
            />
          }
          ariaLabel={t('transfers:ariaTable')}
          empty={
            chips.length > 0 ? (
              <NoResultsState onLimpar={limparFiltros} />
            ) : (
              <EmptyState
                icon={ArrowLeftRight}
                title={t('transfers:empty.title')}
                description={t('transfers:empty.description')}
                action={botaoNovo}
              />
            )
          }
        />
      </div>

      <TransferFormDrawer
        // key: cada abertura remonta o form (nova intenção/chave de idempotência).
        key={drawerAberto ? 'aberto' : 'fechado'}
        open={drawerAberto}
        onOpenChange={setDrawerAberto}
      />
    </>
  )
}
